import React, { useState } from 'react';
import { Tooltip, OverlayTrigger, Modal } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';

//hoc
import withLocalization from '~/hoc/withLocalization';
import applicationRouter from '~/hoc/applicationRouter';

//assets
import CloseModalIcon from '../../../../assets/img/CloseApproveModalIcon.svg';
import moment from 'moment';

const durationsType = {
    D: 'dayShift',
    A: 'afternoonShift',
    N: 'nightShift',
};

const shiftLabels = { D: 'Day', A: 'Afternoon', N: 'Night' };

const DayWiseResourcePlan = ({ plans = {}, headers = [], schedulerData, groupDetails, skillDetails, t, commonStore }) => {
    const [modalData, setModalData] = useState(null);

    let cellWidth = schedulerData.getContentCellWidth();

    const getGroupDetailsMap = new Map();
    groupDetails?.forEach(ele => {
        getGroupDetailsMap.set(ele.code, ele);
    });

    const getSkillDetailsMap = new Map();
    skillDetails?.forEach(ele => {
        getSkillDetailsMap.set(ele.id, ele);
    });

    const filteredUsers = new Set();
    schedulerData?.resources?.forEach(ele => {
        filteredUsers.add(ele.id);
    });

    const eventMap = new Map();
    schedulerData?.events?.forEach(event => {
        const { resourceId, id: date } = event;
        if (!filteredUsers.has(resourceId)) return;
        if (!eventMap.has(resourceId))
            eventMap.set(resourceId, new Map());
        if (!eventMap.get(resourceId).has(date))
            eventMap.get(resourceId).set(date, event);
    });

    const hasSpecificShiftOnDate = (userId, date, companyShiftId) => {
        return eventMap.get(userId)?.get(date)?.shiftId === companyShiftId;
    };

    const getCategoryAllocated = (entry, date, companyShiftId) => {
        const userIds = entry.type === 'group' ? (getGroupDetailsMap.get(entry.value)?.userIds || []) : (getSkillDetailsMap.get(entry.value)?.userIds || []);
        return userIds.filter(uid => hasSpecificShiftOnDate(uid, date, companyShiftId)).length;
    };

    const renderModal = () => {
        if (!modalData) return null;
        const dateTimeRules = commonStore.getDateTimeRules();
        const { date, keyName, keyLabel, shifts } = modalData;

        return (
            <Modal show onHide={() => setModalData(null)} size="lg" centered>
                <Modal.Header>
                    <Modal.Title className="fs15">{moment(date).format(dateTimeRules.short_date_format)} — {t(keyLabel)}</Modal.Title>
                    <img
                        src={CloseModalIcon}
                        className="cursor-pointer"
                        onClick={() => setModalData(null)}
                        alt='close-btn'
                    />
                </Modal.Header>
                <Modal.Body>
                    {Object.keys(shifts).length === 0 && (
                        <span className="text-muted fst-italic">{t('No shifts planned')}</span>
                    )}
                    {Object.entries(shifts).map(([shiftId, shiftEntry]) => {
                        const parsedShiftId = parseInt(shiftId);
                        const shiftName = commonStore?.getCompanyShift(parsedShiftId)?.shift_name || 'No Shifts details found';
                        const categories = shiftEntry.categoryWiseCount || {};
                        return (
                            <div key={shiftId} className="mb-3 border rounded p-2">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="fw-bold fs15">{shiftName}</span>
                                    <span className="badge bg-secondary">
                                        {t('Planned')}: {shiftEntry.headCount}
                                    </span>
                                </div>
                                {Object.keys(categories).length === 0 ? (
                                    <span className="text-muted fst-italic fs-6">{t('No breakdown added')}</span>
                                ) : (
                                    Object.entries(categories).map(([key, entry]) => {
                                        const allocated = getCategoryAllocated(entry, date, parsedShiftId);
                                        const name = entry.type === 'group'
                                            ? (getGroupDetailsMap.get(entry.value)?.name || entry.label)
                                            : (getSkillDetailsMap.get(entry.value)?.name || entry.label);
                                        return (
                                            <div key={key} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                                <span>
                                                    {entry.type === 'group' ? '👥' : '🔧'} {name}
                                                </span>
                                                <span className={`fw-bold ${allocated >= entry.count ? 'text-success' : 'text-danger'}`}>
                                                    {allocated}/{entry.count}
                                                </span>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        );
                    })}
                </Modal.Body>
            </Modal>
        );
    };

    const renderCell = (date) => {
        const dayPlans = plans?.[date];

        if (!dayPlans) {
            return (
                <div className="resource-cell d-flex flex-column align-items-center gap-1" style={{ borderRight: '1px solid #e0e0e0' }}>
                    {Object.entries(durationsType).map(([key], idx) => (
                        <div key={idx} className={`duration-count fs12 ${key.toLowerCase()} plan-under-target`}>
                            <span className="label me-1">{key}</span>
                            <span className="count">0/0</span>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="resource-cell d-flex flex-column align-items-center gap-1" style={{ borderRight: '1px solid #e0e0e0' }}>
                {Object.entries(durationsType).map(([key, keyName], idx) => {
                    const { headCount = 0, categoryWiseCount = null, shifts = {} } = dayPlans[keyName] || {};
                    const typeCount = headCount || 0;
                    const categoryCount = categoryWiseCount ? Object.values(categoryWiseCount).reduce((p, v) => p + v.count, 0) : 0;
                    const groupCount = categoryWiseCount ? Object.values(categoryWiseCount).reduce((p, v) => p + (v.type === 'group' ? v.count : 0), 0) : 0;
                    const skillCount = categoryWiseCount ? Object.values(categoryWiseCount).reduce((p, v) => p + (v.type === 'skill' ? v.count : 0), 0) : 0;
                    const normalCount = typeCount - categoryCount;

                    const countedUsers = new Set();
                    let groupAllocated = 0, skillAllocated = 0, normalAllocated = 0;

                    Object.entries(shifts).forEach(([shiftId, shiftEntry]) => {
                        const parsedShiftId = parseInt(shiftId);
                        const shiftCats = shiftEntry.categoryWiseCount;
                        if (shiftCats) {
                            Object.values(shiftCats).forEach(ele => {
                                const userids = ele.type === 'group' ? (getGroupDetailsMap.get(ele.value)?.userIds || []) : (getSkillDetailsMap.get(ele.value)?.userIds || []);
                                userids.forEach(uid => {
                                    if (hasSpecificShiftOnDate(uid, date, parsedShiftId) && !countedUsers.has(uid)) {
                                        if (ele.type === 'group') groupAllocated++;
                                        else skillAllocated++;
                                        countedUsers.add(uid);
                                    }
                                });
                            });
                        }
                        eventMap.forEach((_, userId) => {
                            if (hasSpecificShiftOnDate(userId, date, parsedShiftId) && !countedUsers.has(userId)) {
                                normalAllocated++;
                                countedUsers.add(userId);
                            }
                        });
                    });

                    const allocated = groupAllocated + skillAllocated + normalAllocated;

                    if (!typeCount) {
                        return (
                            <div key={idx} className={`duration-count fs12 ${key.toLowerCase()} plan-under-target`}>
                                <span className="label me-1">{key}</span>
                                <span className="count">0/0</span>
                            </div>
                        );
                    }

                    return (
                        <OverlayTrigger
                            key={idx}
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    <div>{t('Group allocation')}- {groupAllocated}/{groupCount}</div>
                                    <div>{t('Skill allocation')}- {skillAllocated}/{skillCount}</div>
                                    {normalCount > 0 && <div>{t('Normal allocation')}- {normalAllocated}/{normalCount}</div>}
                                </Tooltip>
                            }
                        >
                            <div
                                className={`duration-count cursor-pointer fs12 ${key.toLowerCase()} ${typeCount && allocated === typeCount ? 'plan-on-target' : allocated > typeCount ? 'plan-over-target' : 'plan-under-target'}`}
                                onClick={() => setModalData({ date, keyName, keyLabel: shiftLabels[key], shifts })}
                            >
                                <span className="label me-1">{key}</span>
                                <span className="count">{allocated}/{typeCount}</span>
                            </div>
                        </OverlayTrigger>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            {renderModal()}
            <tbody>
                <tr style={{ borderLeft: '1px solid #e0e0e0' }}>
                    {headers.map((header, index) => {
                        const date = header.time.split(' ')[0];
                        return (
                            <td key={index} style={{ width: cellWidth }} className="resource-plan-cell">
                                {renderCell(date)}
                            </td>
                        );
                    })}
                </tr>
            </tbody>
        </>
    );
};

export default inject('userStore', 'commonStore', 'scheduleStore', 'clientStore', 'rosterStore', 'userGroupStore', 'resourcePlanStore', 'skillsStore')(withLocalization(applicationRouter(observer(DayWiseResourcePlan))));
